"use client";

import { useMemo, useState } from "react";

/** YYYY-MM-DD for a UTC date. */
export function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** Today's key in the viewer's local time. */
export function todayKey(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

type Props = {
  locale: string;
  /** When this YYYY-MM-DD changes, the grid jumps to that day's month. */
  focus?: string;
  /** Visual classes for a day cell. */
  dayClass: (key: string, isPast: boolean) => string;
  /** Whether a day is non-clickable (past days are disabled unless allowPast). */
  isDisabled?: (key: string, isPast: boolean) => boolean;
  /** Allow selecting days before today (e.g. for reports over past periods). */
  allowPast?: boolean;
  /** Small content under the day number (e.g. the price). */
  renderSub?: (key: string, isPast: boolean) => React.ReactNode;
  onSelect: (key: string) => void;
};

/**
 * A month grid with prev/next navigation and localized labels. Purely
 * presentational: the parent decides how each day looks and what a click means.
 * Shared by the admin availability calendar and the booking date picker.
 */
export function CalendarGrid({
  locale,
  focus,
  dayClass,
  isDisabled,
  allowPast = false,
  renderSub,
  onSelect,
}: Props) {
  const today = todayKey();
  const initKey = focus ?? today;
  const [cursor, setCursor] = useState({
    y: Number(initKey.slice(0, 4)),
    m: Number(initKey.slice(5, 7)) - 1,
  });

  // Jump to the focused day's month when it changes (e.g. typed in a field).
  // Adjusting state during render is the React-recommended alternative to an
  // effect for "reset state when a prop changes".
  const [prevFocus, setPrevFocus] = useState(focus);
  if (focus && focus !== prevFocus) {
    setPrevFocus(focus);
    setCursor({ y: Number(focus.slice(0, 4)), m: Number(focus.slice(5, 7)) - 1 });
  }

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(Date.UTC(cursor.y, cursor.m, 1))),
    [cursor, locale],
  );

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, {
      weekday: "short",
      timeZone: "UTC",
    });
    // 2024-01-01 is a Monday.
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(Date.UTC(2024, 0, 1 + i))),
    );
  }, [locale]);

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(cursor.y, cursor.m, 1));
    const lead = (first.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
    const daysInMonth = new Date(
      Date.UTC(cursor.y, cursor.m + 1, 0),
    ).getUTCDate();
    const out: (string | null)[] = Array(lead).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      out.push(ymd(new Date(Date.UTC(cursor.y, cursor.m, d))));
    }
    return out;
  }, [cursor]);

  function shiftMonth(delta: number) {
    setCursor((c) => {
      const d = new Date(Date.UTC(c.y, c.m + delta, 1));
      return { y: d.getUTCFullYear(), m: d.getUTCMonth() };
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="grid h-9 w-9 place-items-center rounded-full text-stone hover:bg-forest-50 hover:text-forest-600"
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="font-display text-base font-semibold capitalize text-bark">
          {monthLabel}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="grid h-9 w-9 place-items-center rounded-full text-stone hover:bg-forest-50 hover:text-forest-600"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-xs font-medium text-stone">
        {weekdays.map((w) => (
          <div key={w} className="py-1 capitalize">
            {w}
          </div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((key, i) => {
          if (!key) return <div key={`b${i}`} />;
          const day = Number(key.slice(8));
          const isPast = key < today;
          const disabled =
            (!allowPast && isPast) || (isDisabled?.(key, isPast) ?? false);
          const sub = renderSub?.(key, isPast);
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(key)}
              className={`flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg border text-sm leading-none transition-colors disabled:cursor-default ${dayClass(key, isPast)}`}
            >
              <span>{day}</span>
              {sub != null && (
                <span className="text-[9px] font-medium leading-none">
                  {sub}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
